import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
    AggregateService,
    EcapCoreUserAccessManagementService,
    GqlQueryBuilderService,
    PlatformDataService,
    ShellDomainProvider,
    SqlQueryBuilderService,
    UtilityService
} from '../../shared/standalone-packages/ecap3/core';
import { upperFirst } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';
import { Filter, GqlWatchResponse, IAppDataTablePageData } from '../../shared/models';
import { GqlQueryCommandService } from '../../shared/services/gql-query-command.service';
import {
    CreateUserBuisnessCommand,
    PersonForUser,
    UpdateUserBuisnessCommand
} from '../model/user-access-management.model';
import { DatatableModel } from '../../shared/standalone-packages/ecap3/app-platform-datatable';
import {
    IAppPlatformUserAccessManagement,
    PlatfromUserAccssManagementService
} from '../../shared/standalone-packages/ecap3/app-platform-user-access-management';
import { UrlResolverService } from '../../shared/services/url/url-resolver.service';

export const DATE_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY'
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY'
    }
};

@Injectable()
export class AppUserManagementService implements IAppPlatformUserAccessManagement {
    private readonly urlResolver = inject(UrlResolverService);
    header: any = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    public countryCodeDataList: any[] = [];
    currentUserForQualityCheckModal = new BehaviorSubject<Number>(null);

    constructor(
        private sqlQueryBuilderService: SqlQueryBuilderService,
        private gqlQueryCommandService: GqlQueryCommandService,
        private utilityService: UtilityService,
        private aggregateService: AggregateService,
        private platfromUserAccssManagementService: PlatfromUserAccssManagementService,
        private platformDataService: PlatformDataService,
        private userAccessManagementService: EcapCoreUserAccessManagementService,
        private gqlQueryBuilderService: GqlQueryBuilderService,
        private http: HttpClient
    ) {}

    resendActivationEmail(value): any {
        return this.platfromUserAccssManagementService.resendActivationEmail(value && value.CreatedBy).pipe(
            map(response => {
                return response;
            })
        );
    }

    noWhitespaceValidator(control: FormControl) {
        if (control && control.value && !control.value.replace(/\s/g, '').length) {
            control.setValue('');
        }
        return null;
    }

    checkRoleExist(roleName) {
        return this.http.get(`${ShellDomainProvider['api'].Security}SecurityQuery/RoleExists?RoleName=${roleName}`, {
            headers: this.header,
            withCredentials: true,
            observe: 'response'
        });
    }

    createRole(role) {
        return this.userAccessManagementService.CreateRole(role).pipe(map(response => response));
    }

    createUser(userInfo: CreateUserBuisnessCommand) {
        const validUserInfo = userInfo;
        validUserInfo['Language'] = this.utilityService.getCurrentLanguageValue();
        return this.userAccessManagementService.CreateUser(validUserInfo).pipe(map(response => response));
    }

    updateUser(userInfo: UpdateUserBuisnessCommand): Observable<any> {
        return this.http
            .post(`${ShellDomainProvider['api'].Security}SecurityCommand/UpdateUser`, userInfo, {
                headers: this.header,
                withCredentials: true,
                observe: 'response'
            })
            .pipe(map((response: any) => response));
    }

    updatePerson(personInfo, ItemId) {
        return this.gqlQueryCommandService.gqlUpdate(SCConfig.Entity.Person, personInfo, ItemId);
    }

    getPersonByUserId(userId) {
        return this.getPersonByUserIds([userId]);
    }

    getPersonByUserIds(userIds: string[], orderBy = 'CreateDate: -1'): Observable<any> {
        const filters: Filter[] = [
            new Filter('Tags', '=', SCConfig.Tags.Person),
            new Filter('CreatedBy', 'in', userIds)
        ];

        const fields = SCConfig.UserReadableDatas.Person;
        return this.gqlQueryCommandService
            .gqlWatchQuery<PersonForUser>(SCConfig.Entity.Person, fields, filters, orderBy)
            .pipe(map(response => response.Data));
    }

    topFunction() {
        document.getElementById('container-3').scrollTop = 0;
    }

    getUpdatedUserInfo(userList): Observable<any> {
        const callList = [];
        callList.push(this.getPersonByUserIds(userList));
        return combineLatest(callList).pipe(
            map((res: any[]) => {
                const persons = res[0];
                userList.forEach(user => {
                    persons.forEach(person => {
                        if (person['CreatedBy'] !== user['ItemId']) {
                            return;
                        }
                        user['PersonId'] = person['ItemId'];
                        user['ProfileImage'] = person['ProfileImageId'];
                    });
                });
                this.topFunction();
                return userList;
            })
        );
    }

    getCountryList() {
        return this.aggregateService.getAllCountryDataByAggregate().pipe(
            map((data: any) => {
                this.setCountryCodeDataList(data);
                return data;
            })
        );
    }

    setCountryCodeDataList(data: any) {
        Object.keys(data).forEach(i => {
            this.countryCodeDataList.push({
                country: data[i].country,
                dialcode: data[i].dialcode,
                code: i
            });
        }); // for loop ends here
    }

    getUserListForDatatable(
        datatableModel: DatatableModel<PersonForUser>,
        searchKey?: string
    ): Observable<IAppDataTablePageData<PersonForUser>> {
        const payload = {
            SearchFilters: {},
            PageNumber: datatableModel.CurrentPageNumber,
            PageLimit: 10,
            OrderBy: datatableModel.SortBy,
            SortOrder: upperFirst(datatableModel.SortDir + 'ending'),
            PropertyFromPerson: [
                'ItemId',
                'DisplayName',
                'Email',
                'PhoneNumber',
                'Organization',
                'Designation',
                'LastLogInTime',
                'Active',
                'ProfileImage',
                'IsVerified',
                'ExternalCalendarSyncedDate'
            ]
        };
        const filters = [
            new Filter('Roles', '!=', `'system_admin'`),
            new Filter('SiteId', '!=', `'` + SCConfig.UploadIpexSiteId + `'`)
        ];
        const fields = SCConfig.UserReadableDatas.Person;
        if (searchKey) {
            payload.SearchFilters = {
                DisplayName: searchKey,
                Email: searchKey
            };
            filters.push(new Filter(null, 'raw', `$or: [{DisplayName: /${searchKey}/i}, {Email: /${searchKey}/i}]`));
        }
        return this.gqlQueryCommandService.gqlWatchQueryWithDatatableModel(
            SCConfig.Entity.Person,
            fields,
            filters,
            `${datatableModel.SortBy}: ${datatableModel.SortDir === 'asc' ? 1 : -1}`,
            datatableModel
        );
        // return this.userAccessManagementService.getUserList(payload).pipe(
        //     map((response: HttpResponse<GetUserListResponse>) => {
        //         const body = response.body;
        //         return this.gqlQueryCommandService.parseGQLDataTableResponseData(
        //             new GqlWatchResponse(body.Users, body.TotalCount),
        //             datatableModel as any
        //         );
        //     })
        // );
    }

    getUserListForDatatableByEndpoint(
        datatableModel: DatatableModel<PersonForUser>,
        searchKey?: string
    ): Observable<IAppDataTablePageData<PersonForUser>> {
        const payload = {
            PageNumber: datatableModel.CurrentPageNumber + 1, // adding 1 because api is 1 based index
            PageLimit: datatableModel.PageSize || 10,
            OrderBy: datatableModel.SortBy || 'CreatedDate',
            SortOrder: upperFirst(datatableModel.SortDir + 'ending') || 'Descending',
            SearchKey: searchKey
                ? [
                      { Key: 'DisplayName', Value: searchKey },
                      { Key: 'Email', Value: searchKey }
                  ]
                : [],
            UploadIpexSiteId: SCConfig.UploadIpexSiteId,
            IncludeInactive: true,
            ExcludeRoles: ['system_admin'],
            IncludeRoles: [],
            Organization: '',
            Department: '',
            IsVerified: true,
            LastLoginFrom: null,
            LastLoginTo: null
        };

        const url = `${this.urlResolver.getPcxQueryUrl()}GetPersonList`;
        return this.http.post(url, payload, SCConfig.HttpCommonOptions).pipe(
            map((response: any) => {
                response.data = response.Data || [];
                const personsData = response.Data || [];
                const totalCount = response.TotalCount || 0;

                return this.gqlQueryCommandService.parseGQLDataTableResponseData(
                    new GqlWatchResponse(personsData, totalCount),
                    datatableModel
                );
            })
        );
    }
    deleteUser(payload) {
        const uri = `${this.urlResolver.getPcxCommandUrl()}DeleteData`;
        const request = {
            Uri: uri,
            Payload: payload
        };
        return this.http.post(uri, request.Payload, SCConfig.HttpCommonOptions) as any;
    }
}
